import type { EmbedErrorDetail } from './InsidedEmbedElement.types'
import { ensureResourcesLoaded, getEmbedGlobal } from './ResourceLoader'

/**
 * `<gs-cc-embed>` — Loads and renders an embedded community widget.
 *
 * Fetches widget HTML from the backend, hydrates it via declarative
 * shadow DOM, and binds Preact components.
 *
 * @fires embed-loading — Fetch started
 * @fires embed-loaded  — Widget injected and bound
 * @fires widget-ready  — Preact components hydrated
 * @fires embed-error   — Loading or binding failed
 */
export class InsidedEmbedElement extends HTMLElement {
  private abortController: AbortController | null = null

  /**
   * Attributes that trigger a widget re-fetch when changed.
   * Must be lowercase — HTML `setAttribute` lowercases names per spec.
   *
   * @param page        — (required) Community page to load (e.g. `"homepage"`)
   * @param type        — (required) Widget variant (e.g. `"header_navigation"`)
   */
  static get observedAttributes(): string[] {
    return ['page', 'type']
  }

  /**
   * Re-fetches the widget whenever an observed attribute changes on a
   * connected element. No-ops if the value hasn't actually changed.
   */
  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return
    if (this.isConnected) {
      this.loadWidget()
    }
  }

  connectedCallback(): void {
    this.loadWidget()
  }

  disconnectedCallback(): void {
    this.abort()
    this.innerHTML = ''
  }

  /** The community page whose widgets should be loaded (e.g. `"homepage"`). */
  private get page(): string {
    return this.getAttribute('page') ?? ''
  }

  /**
   * The widget variant to render (e.g. `"header_navigation"`).
   * Named `type` in the HTML attribute and query param; aliased here
   * as `widgetType` to avoid confusion with `productType`.
   */
  private get widgetType(): string {
    return this.getAttribute('type') ?? ''
  }

  private buildRequestBody(): Record<string, unknown> {
    const global = getEmbedGlobal()
    const body: Record<string, unknown> = {
      page: this.page,
      type: this.widgetType
    }

    // Forward embed-level context to the backend
    body.context = {
      communityUrl: global.communityUrl,
      productType: global.productType,
      language: global.language,
      ...global.context
    }

    return body
  }

  private abort(): void {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  private async loadWidget(): Promise<void> {
    const communityUrl = getEmbedGlobal().communityUrl
    const { page, widgetType } = this

    if (!communityUrl) {
      this.emitError('window.__insidedEmbed.communityUrl is required')
      return
    }

    if (!page || !widgetType) {
      return
    }

    this.abort()
    this.abortController = new AbortController()
    const { signal } = this.abortController

    this.dispatch('embed-loading')

    try {
      await ensureResourcesLoaded(communityUrl)

      if (signal.aborted) return

      const csrfToken = getEmbedGlobal().csrfToken
      if (!csrfToken) {
        this.emitError('CSRF token not available — /widget/resources may not have loaded correctly')
        return
      }

      const headers: Record<string, string> = {
        Accept: 'text/html',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-TOKEN': csrfToken
      }

      const response = await fetch(`${communityUrl}/widget/embed`, {
        method: 'POST',
        headers,
        body: JSON.stringify(this.buildRequestBody()),
        signal
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch widget: ${response.status}`)
      }

      const html = await response.text()

      if (signal.aborted) return

      this.innerHTML = html
      this.initDeclarativeShadowDom()

      this.bindWidgetComponents()
      this.dispatch('embed-loaded')
    } catch (error) {
      if (signal.aborted) return

      this.handleLoadError(error)
    }
  }

  /**
   * Hydrates Preact components inside the widget's shadow DOM.
   *
   * `window.insided.bindPreactComponents` is the same function used
   * internally by the community app to hydrate server-rendered Preact
   * components. It scans the given root for `[data-preact-component]`
   * markers and mounts the corresponding Preact trees.
   *
   * This is available because `ensureResourcesLoaded` loads `preact-app.js`
   * (which registers `window.insided`) before we reach this point.
   */
  private bindWidgetComponents(): void {
    const widgetEl = this.querySelector('gs-cc-widget') ?? this.querySelector('insided-widget')
    const insided = (window as any).insided
    if (widgetEl?.shadowRoot && typeof insided?.bindPreactComponents === 'function') {
      insided.bindPreactComponents(widgetEl.shadowRoot)
      this.dispatch('widget-ready')
    } else {
      this.emitError('Failed to bind widget: shadow root or bindPreactComponents not available')
    }
  }

  private handleLoadError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error)
    const errorObj = error instanceof Error ? error : undefined
    this.emitError(message, errorObj)
  }

  private initDeclarativeShadowDom(): void {
    const widget = this.querySelector('gs-cc-widget') ?? this.querySelector('insided-widget')
    if (!widget || widget.shadowRoot) return

    const template = widget.querySelector<HTMLTemplateElement>('template[shadowrootmode]')
    if (!template) return

    const mode = (template.getAttribute('shadowrootmode') ?? 'open') as ShadowRootMode
    const shadowRoot = widget.attachShadow({ mode })
    shadowRoot.innerHTML = template.innerHTML
    template.remove()
  }

  private dispatch(type: string): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true }))
  }

  private emitError(message: string, error?: Error): void {
    console.error(`[GsCcEmbed] ${message}`, error)
    this.dispatchEvent(
      new CustomEvent<EmbedErrorDetail>('embed-error', {
        bubbles: true,
        composed: true,
        detail: { message, error }
      })
    )
  }
}

if (typeof customElements !== 'undefined') {
  if (!customElements.get('gs-cc-embed')) {
    customElements.define('gs-cc-embed', InsidedEmbedElement)
  }
  // Backward compatibility (registry requires a distinct constructor per name)
  if (!customElements.get('insided-embed')) {
    customElements.define('insided-embed', class extends InsidedEmbedElement {})
  }
}
